import { useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import {
  addOrganisationMember,
  fetchOrganisation,
  fetchOrganisationMembers,
  removeOrganisationMember,
  updateOrganisationMember,
} from "../../../api/organisations";
import { ApiError } from "../../../api/client";
import { Button } from "../../../components/ui/button";
import { useAuth } from "../../../contexts/AuthProvider";
import { organisationRoleLabel } from "../../../lib/auth/formAccess";
import type {
  OrganisationDto,
  OrganisationMemberDto,
  OrganisationRole,
} from "../../../types/organisation";
import {
  AdminShell,
  FieldLabel,
  SelectInput,
  TextInput,
} from "../_components/AdminShell";

const ROLE_OPTIONS: OrganisationRole[] = [
  "org_admin",
  "form_manager",
  "form_viewer",
];

export default function OrganisationDetailPage() {
  const { organisationId } = useParams();
  const numericId = organisationId ? Number(organisationId) : NaN;
  const { canManageOrgMembers } = useAuth();

  const [organisation, setOrganisation] = useState<OrganisationDto | null>(
    null,
  );
  const [members, setMembers] = useState<OrganisationMemberDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<OrganisationRole>("form_manager");
  const [adding, setAdding] = useState(false);

  const canManage = !Number.isNaN(numericId) && canManageOrgMembers(numericId);

  const reload = async () => {
    if (Number.isNaN(numericId)) return;

    setLoading(true);
    setError(null);
    try {
      const [org, memberList] = await Promise.all([
        fetchOrganisation(numericId),
        fetchOrganisationMembers(numericId),
      ]);
      setOrganisation(org);
      setMembers(memberList);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Не удалось загрузить организацию",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Организация";
    void reload();
  }, [numericId]);

  const handleAddMember = async (event: FormEvent) => {
    event.preventDefault();
    if (!canManage || Number.isNaN(numericId)) return;

    const parsedUserId = Number(userId.trim());
    if (!Number.isFinite(parsedUserId)) {
      setActionError("Укажите корректный ID пользователя");
      return;
    }

    setAdding(true);
    setActionError(null);
    try {
      await addOrganisationMember(numericId, {
        userId: Math.trunc(parsedUserId),
        role,
      });
      setUserId("");
      await reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : "Не удалось добавить участника",
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRoleChange = async (
    memberUserId: number,
    nextRole: OrganisationRole,
  ) => {
    if (!canManage || Number.isNaN(numericId)) return;

    setActionError(null);
    try {
      await updateOrganisationMember(numericId, memberUserId, {
        role: nextRole,
      });
      await reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : "Не удалось обновить роль",
      );
    }
  };

  const handleRemoveMember = async (memberUserId: number) => {
    if (!canManage || Number.isNaN(numericId)) return;
    if (!window.confirm(`Удалить участника #${memberUserId}?`)) return;

    setActionError(null);
    try {
      await removeOrganisationMember(numericId, memberUserId);
      await reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : "Не удалось удалить участника",
      );
    }
  };

  if (Number.isNaN(numericId)) {
    return (
      <AdminShell compact title="Организация">
        <p className="font-onest text-sm text-red-700">Некорректный ID</p>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      compact
      title={organisation?.name ?? "Организация"}
      subtitle={organisation?.slug}
      backLink={{ to: "/admin/organisations", label: "К организациям" }}
    >
      {error ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="font-onest text-sm text-red-700">{error}</p>
        </div>
      ) : null}

      {actionError ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="font-onest text-sm text-red-700">{actionError}</p>
        </div>
      ) : null}

      {canManage ? (
        <form
          onSubmit={(event) => void handleAddMember(event)}
          className="mb-4 rounded-xl border border-slate-200/80 bg-white p-4 shadow-card-lg"
        >
          <h2 className="font-onest text-sm font-semibold text-ink">
            Добавить участника
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem_auto] sm:items-end">
            <div>
              <FieldLabel htmlFor="member-user-id" size="compact">
                ID пользователя
              </FieldLabel>
              <TextInput
                id="member-user-id"
                size="compact"
                value={userId}
                onChange={setUserId}
              />
            </div>
            <div>
              <FieldLabel htmlFor="member-role" size="compact">
                Роль
              </FieldLabel>
              <SelectInput
                id="member-role"
                size="compact"
                value={role}
                onChange={(value) => setRole(value as OrganisationRole)}
              >
                {ROLE_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {organisationRoleLabel(item)}
                  </option>
                ))}
              </SelectInput>
            </div>
            <Button
              type="submit"
              className="px-4 py-2 text-sm"
              disabled={adding}
            >
              {adding ? "Добавление…" : "Добавить"}
            </Button>
          </div>
        </form>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-card-lg">
        {loading ? (
          <div className="px-4 py-8 text-center font-onest text-sm text-slate-500">
            Загрузка…
          </div>
        ) : members.length === 0 ? (
          <div className="px-4 py-8 text-center font-onest text-sm text-slate-500">
            Участников пока нет
          </div>
        ) : (
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="px-4 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Пользователь
                </th>
                <th className="px-4 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Роль
                </th>
                {canManage ? (
                  <th className="px-4 py-2 text-right font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Действия
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.userId} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-onest text-sm text-slate-800">
                    <div>#{member.userId}</div>
                    {member.name || member.email ? (
                      <div className="text-xs text-slate-500">
                        {[member.name, member.email]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2">
                    {canManage ? (
                      <SelectInput
                        size="compact"
                        value={member.role}
                        onChange={(value) =>
                          void handleRoleChange(
                            member.userId,
                            value as OrganisationRole,
                          )
                        }
                      >
                        {ROLE_OPTIONS.map((item) => (
                          <option key={item} value={item}>
                            {organisationRoleLabel(item)}
                          </option>
                        ))}
                      </SelectInput>
                    ) : (
                      <span className="font-onest text-sm text-slate-700">
                        {organisationRoleLabel(member.role)}
                      </span>
                    )}
                  </td>
                  {canManage ? (
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => void handleRemoveMember(member.userId)}
                        className="font-onest text-xs font-medium text-red-600 hover:underline"
                      >
                        Удалить
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}
